/**
 * Slice D (backlog F2 / #220 follow-up): central, plain-English error copy.
 *
 * Backend route handlers throw structured errors carrying a machine `code`. Two
 * shapes reach the SPA via `ApiHttpError.body`:
 *   - Materials backend: `{ error: <human>, code: 'INSUFFICIENT_STOCK', … }`
 *     (the real code is in `.code`).
 *   - CB AppError handler: `{ error: <code>, code: 'CONFLICT'|'VALIDATION_ERROR',
 *     details }` (the real, specific code is in `.error`; `.code` is the generic
 *     family). See backend src/middleware/errorHandler.ts + src/lib/errors.ts.
 *
 * `friendlyErrorMessage` resolves the code off EITHER position, maps it to
 * friendly copy, and otherwise degrades BY STATUS to a sensible generic. It
 * NEVER returns raw JSON or an `API <status>:` string — that technical leak is
 * exactly what this work removes (e.g. the protected-day delete guard, the
 * unsupported-upload rejection).
 */

import { ApiHttpError } from './api';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Friendly copy keyed by the backend error `code` (or, for CB AppErrors, the
 * specific code carried in `body.error`). Only user-facing codes need an entry;
 * everything else falls back by status.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // ─── Materials: stock / inventory ──────────────────────────────────────────
  INSUFFICIENT_STOCK: 'Not enough stock on hand to complete this action.',
  NOT_RELEASABLE:
    "That reservation can't be released — it may already have been used, checked out, or returned. Refresh and try again.",

  // ─── Materials: orders / purchasing ────────────────────────────────────────
  ORDER_NOT_DRAFT: 'This order has already been submitted, so it can no longer be changed.',
  ORDER_EMPTY: 'Add at least one item to this order before submitting it.',
  BAD_PRICE: "That price option can't be used — please choose a different price.",
  JOB_NOT_FOUND: 'That job could not be found — it may have changed. Please refresh and try again.',

  // ─── Materials: catalogue ──────────────────────────────────────────────────
  CATALOG_DUPLICATE_ACTIVE: 'Another active product already has this name + pack size.',
  ALREADY_RESOLVED: 'That item has already been dealt with. Refresh to see the latest.',

  // ─── Materials: deliveries ─────────────────────────────────────────────────
  INCOMPLETE_DELIVERY: 'This delivery is incomplete — check the quantities and quality checks.',

  // ─── Uploads / extraction ──────────────────────────────────────────────────
  // Synthetic code + the 502 analyse/extract failures: all mean "we can't read
  // this file". The zod "Unsupported mime_type" rejection is matched separately.
  UNSUPPORTED_FILE_TYPE:
    "That file type isn't supported — please upload a PDF, image, or Word document.",
  ANALYZE_FAILED: "That file type isn't supported — please upload a PDF, image, or Word document.",
  TEST_EXTRACT_FAILED:
    "We couldn't read that sample file — please check it's a clear PDF, image, or Word document and try again.",

  // ─── Xero integration ──────────────────────────────────────────────────────
  XERO_REAUTH_REQUIRED:
    'Xero needs reconnecting before this can be sent. Please reconnect Xero and try again.',
  SUPPLIER_NOT_MAPPED:
    "This supplier isn't linked to a Xero contact yet — choose one before sending the order.",

  // ─── CB: scheduling / assignments ──────────────────────────────────────────
  // Thrown when a planner tries to remove a scheduled day for a worker who has
  // already clocked in today (routes/assignments.ts protected-segment guard).
  clock_out_segment_protected:
    "This worker is clocked in today, so their shift can't be removed. Remove a different day instead.",
  DRIVE_UNAVAILABLE: 'Google Drive is temporarily unavailable. Please try again shortly.',
  GEOCODE_FAILED: "We couldn't find that address — please check it and try again.",

  SUBCONTRACTOR_HISTORY:
    'This subcontractor has access or retained history. Deactivate them instead of deleting them.',
  COMPANY_HAS_PEOPLE:
    'This company still has linked people, including inactive people. Keep the company with their history.',
  COMPANY_INACTIVE: 'Choose an active company.',
  COMPANY_NO_INITIALS: 'Add company initials before creating a subcontractor.',
  SUBCONTRACTOR_IDENTITY_CONFLICT:
    'The subcontractor identifier or email is already in use. Check the company initials and email.',

  // ─── CB: generic families (friendlier than the raw validation envelope) ────
  VALIDATION_ERROR: 'Please check the details you entered and try again.',
  BAD_BODY: 'Please check the details you entered and try again.',
  BAD_QUERY: 'Please check the details you entered and try again.',
  BAD_PARAM: 'Please check the details you entered and try again.',
  RATE_LIMITED: 'Too many requests — please wait a moment and try again.',
};

/**
 * CB AppErrors whose `body.error` is a DYNAMIC, already-human sentence (e.g.
 * "Scheduled work continues until <date>"). For these we surface the message
 * itself rather than a static generic, because the specific detail is the point.
 * Still never raw JSON / `API <status>:` — these are plain sentences.
 */
const DYNAMIC_MESSAGE_CODES = new Set([
  'INVALID_TRANSITION',
  'WTR_VIOLATION',
  'WORKING_TIME_ERROR',
]);

/** Pulls a recognised code from either `body.code` or `body.error`. */
function resolveCode(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    for (const key of ['code', 'error'] as const) {
      const v = b[key];
      if (typeof v === 'string' && v in ERROR_MESSAGES) return v;
    }
  }
  return undefined;
}

/** Detects the zod "Unsupported mime_type" rejection that has no dedicated code. */
function mentionsUnsupportedFile(body: unknown): boolean {
  if (body == null) return false;
  let serialised: string;
  try {
    serialised = typeof body === 'string' ? body : JSON.stringify(body);
  } catch {
    return false;
  }
  return /unsupported (mime_type|mime|file type)/i.test(serialised);
}

/** Returns a dynamic, already-human message for the codes that carry one. */
function dynamicMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const code = typeof b.code === 'string' ? b.code : undefined;
    if (code && DYNAMIC_MESSAGE_CODES.has(code)) {
      const msg = b.error ?? b.message;
      if (typeof msg === 'string' && msg.trim().length > 0) return msg;
    }
  }
  return undefined;
}

/** A sensible, friendly generic keyed on HTTP status — the last resort. */
function statusFallback(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404)
    return 'That item could not be found — it may have changed. Please refresh and try again.';
  if (status === 409)
    return "That action conflicts with the item's current state. Please refresh and try again.";
  if (status === 413) return 'That file is too large to upload.';
  if (status === 415) return ERROR_MESSAGES.UNSUPPORTED_FILE_TYPE;
  if (status === 422) return "That change isn't allowed right now. Please refresh and try again.";
  if (status === 429) return 'Too many requests — please wait a moment and try again.';
  if (status >= 400 && status < 500) return 'Please check the details you entered and try again.';
  if (status >= 500) return 'Something went wrong on our end. Please try again shortly.';
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Resolve any thrown value to friendly, plain-English copy. For an ApiHttpError
 * it looks up the backend code, special-cases unsupported uploads + dynamic
 * transition messages, then falls back by status. NEVER returns raw JSON or an
 * `API <status>:` string. Non-ApiHttpError values get the generic message.
 */
export function friendlyErrorMessage(error: unknown): string {
  if (error instanceof ApiHttpError) {
    // Unsupported-upload detection runs first: the backend rejects bad file
    // types as a generic zod `BAD_BODY`, so a plain code lookup would mask it.
    if (mentionsUnsupportedFile(error.body)) return ERROR_MESSAGES.UNSUPPORTED_FILE_TYPE;
    const code = resolveCode(error.body);
    if (code) return ERROR_MESSAGES[code];
    const dynamic = dynamicMessage(error.body);
    if (dynamic) return dynamic;
    return statusFallback(error.status);
  }
  return GENERIC_ERROR_MESSAGE;
}
